/**
 * HealthBanner Component
 *
 * Displays a system health status banner when services are degraded or unhealthy.
 * Visible to ALL users (not just admins) on ALL pages including login.
 *
 * Features:
 * - Fixed at top of viewport, pushes other elements down via CSS variable
 * - Auto-refreshes every 60 seconds
 * - Dismissible (resets on page refresh, re-shows if status worsens)
 * - Color-coded: yellow for degraded, red for unhealthy
 *
 * CSS Variable: Sets --health-banner-height on :root for other components to use
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { getPublicHealthStatus, PublicHealthStatus } from '../services/api';

// Status severity for comparison (higher = worse)
const STATUS_SEVERITY: Record<string, number> = {
  healthy: 0,
  degraded: 1,
  unhealthy: 2,
};

const HealthBanner: React.FC = () => {
  const { t } = useTranslation('common');
  const [healthStatus, setHealthStatus] = useState<PublicHealthStatus | null>(null);
  // Track what severity level was dismissed (resets on page refresh)
  const [dismissedSeverity, setDismissedSeverity] = useState<number>(-1);
  const [isLoading, setIsLoading] = useState(true);
  const bannerRef = useRef<HTMLDivElement>(null);

  // Calculate current severity
  const currentSeverity = healthStatus ? (STATUS_SEVERITY[healthStatus.status] ?? 0) : 0;

  // Determine if banner should show (used for both rendering and CSS variable)
  const shouldShow = !isLoading &&
    healthStatus &&
    healthStatus.status !== 'healthy' &&
    currentSeverity > dismissedSeverity;

  // Update CSS variable when banner visibility changes
  useEffect(() => {
    const updateCssVariable = () => {
      if (shouldShow && bannerRef.current) {
        const height = bannerRef.current.offsetHeight;
        document.documentElement.style.setProperty('--health-banner-height', `${height}px`);
      } else {
        document.documentElement.style.setProperty('--health-banner-height', '0px');
      }
    };

    // Update immediately and after a delay (for initial render)
    updateCssVariable();
    const timeoutId = setTimeout(updateCssVariable, 50);

    return () => clearTimeout(timeoutId);
  }, [shouldShow]);

  // Clean up CSS variable on unmount
  useEffect(() => {
    return () => {
      document.documentElement.style.setProperty('--health-banner-height', '0px');
    };
  }, []);

  // Fetch health status
  const fetchHealthStatus = useCallback(async () => {
    try {
      const status = await getPublicHealthStatus();
      setHealthStatus(status);
    } catch (error) {
      // On error, assume healthy to avoid false alarms
      console.warn('[HealthBanner] Failed to fetch health status:', error);
      setHealthStatus({ status: 'healthy', message: null, affected_features: [], last_check_at: null });
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Initial fetch and polling
  useEffect(() => {
    fetchHealthStatus();
    const interval = setInterval(fetchHealthStatus, 60000);
    return () => clearInterval(interval);
  }, [fetchHealthStatus]);

  // Handle dismiss - remembers the severity level that was dismissed
  const handleDismiss = () => {
    if (healthStatus) {
      setDismissedSeverity(currentSeverity);
    }
  };

  // Don't render if not showing
  if (!shouldShow) {
    return null;
  }

  const isDegraded = healthStatus?.status === 'degraded';
  const isUnhealthy = healthStatus?.status === 'unhealthy';

  // Styling based on status
  const bannerClasses = isDegraded
    ? 'bg-yellow-50 border-b border-yellow-300'
    : 'bg-red-50 border-b border-red-300';

  const textClasses = isDegraded
    ? 'text-yellow-800'
    : 'text-red-800';

  const iconClasses = isDegraded
    ? 'text-yellow-600'
    : 'text-red-600';

  const buttonClasses = isDegraded
    ? 'text-yellow-600 hover:text-yellow-800 hover:bg-yellow-100'
    : 'text-red-600 hover:text-red-800 hover:bg-red-100';

  return (
    <div ref={bannerRef} className={`${bannerClasses} px-4 py-3 fixed top-0 left-0 right-0 z-50`}>
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          {/* Icon */}
          {isDegraded ? (
            // Lightning bolt for degraded
            <svg className={`w-5 h-5 flex-shrink-0 ${iconClasses}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          ) : (
            // Warning triangle for unhealthy
            <svg className={`w-5 h-5 flex-shrink-0 ${iconClasses}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          )}

          {/* Message */}
          <span className={`text-sm font-medium ${textClasses}`}>
            {healthStatus?.message || (isDegraded
              ? t('health.degradedPerformance')
              : t('health.servicesExperiencingIssues')
            )}
            {isUnhealthy && (
              <span className="font-normal">. {t('health.workingOnIt')}</span>
            )}
          </span>
        </div>

        {/* Dismiss button */}
        <button
          onClick={handleDismiss}
          className={`flex-shrink-0 p-1 rounded ${buttonClasses} transition-colors`}
          aria-label={t('health.dismissNotification')}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default HealthBanner;
